// src/pages/AcademyShowcase.jsx
import React, { useEffect, useRef, useState } from "react";
import "./AcademyShowcase.css";

export default function AcademyShowcase() {
  const sectionRef = useRef(null);

  const stripWrapperRef = useRef(null);
  const firstRealFaceRef = useRef(null);

  const [inView, setInView] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);

  const [dockPos, setDockPos] = useState({ xPx: 0, yPx: 0, wPx: 140 });
  const [sectionDims, setSectionDims] = useState({ width: 0 });

  /* -----------------------------
     1. Fade / reveal trigger
  ------------------------------*/
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && setInView(true)),
      { threshold: 0.15 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  /* -----------------------------
     2. Scroll progress normalizer
     (start earlier, end a bit earlier)
  ------------------------------*/
  useEffect(() => {
    function handleScroll() {
      if (!sectionRef.current) return;
      const rect = sectionRef.current.getBoundingClientRect();
      const vh = window.innerHeight;

      // moved the start earlier (0.65vh) and end earlier (0.35vh buffer)
      const startLine = vh * 0.65;
      const endLine = -(rect.height - vh * 0.35);
      const raw = (rect.top - startLine) / (endLine - startLine);
      const norm = 1 - raw;
      setScrollProgress(clamp(norm, 0, 1));
    }
    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("resize", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleScroll);
    };
  }, []);

  /* -----------------------------
     3. Measure belt geometry
  ------------------------------*/
  useEffect(() => {
    function measureDock() {
      if (!sectionRef.current || !firstRealFaceRef.current) return;

      const sectionRect = sectionRef.current.getBoundingClientRect();
      const faceRect = firstRealFaceRef.current.getBoundingClientRect();

      setDockPos({
        xPx: faceRect.left - sectionRect.left,
        yPx: faceRect.top - sectionRect.top,
        wPx: faceRect.width,
      });

      const secRect = sectionRef.current.getBoundingClientRect();
      setSectionDims({ width: secRect.width });
    }
    measureDock();
    window.addEventListener("resize", measureDock);
    window.addEventListener("scroll", measureDock, { passive: true });
    return () => {
      window.removeEventListener("resize", measureDock);
      window.removeEventListener("scroll", measureDock);
    };
  }, []);

  /* ---------------------------------
     4. Scroll phases (start Phase B sooner)
  ----------------------------------*/
  const animProgress = 1 - scrollProgress;

  // Phase A is shorter so she settles into the group photo faster
  const phaseA = clamp(animProgress / 0.28, 0, 1);

  // Phase B begins earlier (overlaps slightly with late Phase A)
  const phaseB = clamp((animProgress - 0.18) / 0.82, 0, 1);

  /* ---------------------------------
     5. HERO STUDENT - PHASE A (in card)
  ----------------------------------*/
  const startAX_vw = -60;
  const startAY_px = 20;
  const startAScale = 1.1;

  const endAX_vw = 0;
  const endAY_px = 0;
  const endAScale = 1;

  const aX_vw = lerp(startAX_vw, endAX_vw, easeOutCubic(phaseA));
  const aY_px = lerp(startAY_px, endAY_px, easeOutCubic(phaseA));
  const aScale = lerp(startAScale, endAScale, easeOutCubic(phaseA));

  // fade Phase A out a bit earlier so the handoff feels sooner
  const heroAOpacityIn = phaseA < 0.12 ? lerp(0, 1, phaseA / 0.12) : 1;
  const heroAOpacity = phaseB > 0.05 ? 0 : heroAOpacityIn;

  /* ---------------------------------
     6. HERO STUDENT - PHASE B (float/dock)
  ----------------------------------*/
  const startBX_px = sectionDims.width * 0.2;
  const startBY_px = 180; // slightly sooner vertical approach
  const dockY = dockPos.yPx;
  const dockX = dockPos.xPx - dockPos.wPx * 1.1;

  const bX_px = lerp(startBX_px, dockX, easeInOut(phaseB));
  const bY_px = lerp(startBY_px, dockY, easeInOut(phaseB));

  // slightly reduced final scale so docking completes a hair sooner
  const bScale = lerp(1, 1.45, easeInOut(phaseB));

  const heroBOpacity =
    phaseB <= 0 ? 0 : phaseB < 0.18 ? lerp(0, 1, phaseB / 0.18) : 1;

  /* ---------------------------------
     7. BELT AUTO-SCROLL (kick in earlier)
  ----------------------------------*/
  const heroFinalWidthPx = dockPos.wPx * 1.3;
  const stripLeftPadPx = heroFinalWidthPx * 1.1;

  // start belt activation earlier
  const rowActivation = easeInOut(clamp((phaseB - 0.25) / 0.55, 0, 1));

  const [idleDrift, setIdleDrift] = useState(0);
  useEffect(() => {
    let frame;
    let last = performance.now();
    function tick(now) {
      const dt = now - last;
      last = now;
      setIdleDrift((prev) => prev - (rowActivation > 0.05 ? dt * 0.02 : 0));
      frame = requestAnimationFrame(tick);
    }
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [rowActivation]);

  const stripTranslateX = rowActivation > 0 ? idleDrift : 0;

  return (
    <section className="academy-section" ref={sectionRef}>
      <div className="academy-section-inner">
        {/* ===================== GROUP SHOT (CARD) ===================== */}
        <div className={`academy-proof-media fade-slide ${inView ? "is-visible" : ""}`}>
      <picture>
  {/* Mobile first */}
  <source
    media="(max-width: 800px)"
    srcSet="https://cosacademyassets.s3.us-east-2.amazonaws.com/mobileGROUP.PNG"
  />
  {/* Desktop / default */}
  <img
    className="academy-proof-img"
    src="https://cosacademyassets.s3.us-east-2.amazonaws.com/desktopGROUP.PNG"
    alt="COSMETINK ACADEMY instructors and students"
    loading="eager"
    decoding="async"
    fetchpriority="high"
  />
</picture>

          <div className="academy-proof-gradient" />

          <h2 className="academy-proof-headline desktop-headline">

            <span className="headline-accent">We create the BEST permanent makeup artists</span>
          </h2>

          {/* HERO STUDENT PHASE A */}
          <div
            className="hero-student-phaseA"
            style={{
              opacity: heroAOpacity,
              transform: `
                translateX(${aX_vw}vw)
                translateY(${aY_px}px)
                scale(${aScale})
              `,
            }}
          >
            <img
              src="https://cosacademyassets.s3.us-east-2.amazonaws.com/jeevee.PNG"
              alt="COSMETINK ACADEMY student"
            />
          </div>
        </div>

        {/* MOBILE headline */}
        <h2 className={`academy-proof-headline mobile-headline fade-slide ${inView ? "is-visible" : ""}`}>

          <span className="headline-accent">We create the BEST permanent makeup artists 🫦</span>
        </h2>

        {/* HERO STUDENT PHASE B */}
        <div
          className="hero-student-phaseB"
          style={{
            opacity: heroBOpacity,
            transform: `
              translateX(${bX_px}px)
              translateY(${bY_px}px)
              scale(${bScale})
            `,
          }}
        >
          <img
            src="https://cosacademyassets.s3.us-east-2.amazonaws.com/jeevee.PNG"
            alt="COSMETINK ACADEMY student"
          />
        </div>

        {/* ===================== STUDENT STRIP ===================== */}
        <div
          className={`student-strip-wrapper fade-slide ${inView ? "is-visible" : ""}`}
          ref={stripWrapperRef}
          style={{ marginTop: "clamp(2rem,3vw,3rem)" }}
        >
          <div className="student-strip-mask" style={{ paddingLeft: `${stripLeftPadPx}px` }}>
            <div className="student-strip-track" style={{ transform: `translateX(${stripTranslateX}px)` }}>
              <div className="student-face" ref={firstRealFaceRef}>
                <img
                  src="https://cosacademyassets.s3.us-east-2.amazonaws.com/student3.PNG"
                  alt="COSMETINK ACADEMY student"
                />
              </div>

              <StudentFace src="https://cosacademyassets.s3.us-east-2.amazonaws.com/student4.PNG" />
              <StudentFace src="https://cosacademyassets.s3.us-east-2.amazonaws.com/student5.PNG" />
              <StudentFace src="https://cosacademyassets.s3.us-east-2.amazonaws.com/student6.PNG" />
              <StudentFace src="https://cosacademyassets.s3.us-east-2.amazonaws.com/student7.PNG" />
              <StudentFace src="https://cosacademyassets.s3.us-east-2.amazonaws.com/student9.PNG" />
              <StudentFace src="https://cosacademyassets.s3.us-east-2.amazonaws.com/student10.PNG" />
              <StudentFace src="https://cosacademyassets.s3.us-east-2.amazonaws.com/student11.PNG" />
              <StudentFace src="https://cosacademyassets.s3.us-east-2.amazonaws.com/student2.PNG" />

              {/* duplicate loop */}
              <StudentFace src="https://cosacademyassets.s3.us-east-2.amazonaws.com/student3.PNG" />
              <StudentFace src="https://cosacademyassets.s3.us-east-2.amazonaws.com/student4.PNG" />
              <StudentFace src="https://cosacademyassets.s3.us-east-2.amazonaws.com/student5.PNG" />
              <StudentFace src="https://cosacademyassets.s3.us-east-2.amazonaws.com/student6.PNG" />
              <StudentFace src="https://cosacademyassets.s3.us-east-2.amazonaws.com/student7.PNG" />
              <StudentFace src="https://cosacademyassets.s3.us-east-2.amazonaws.com/student9.PNG" />
              <StudentFace src="https://cosacademyassets.s3.us-east-2.amazonaws.com/student10.PNG" />
              <StudentFace src="https://cosacademyassets.s3.us-east-2.amazonaws.com/student11.PNG" />
              <StudentFace src="https://cosacademyassets.s3.us-east-2.amazonaws.com/student2.PNG" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function StudentFace({ src }) {
  return (
    <div className="student-face">
      <img src={src} alt="COSMETINK ACADEMY student" />
    </div>
  );
}

/* helpers */
function clamp(v, min, max) {
  return v < min ? min : v > max ? max : v;
}
function lerp(a, b, t) {
  return a + (b - a) * t;
}
function easeOutCubic(t) {
  return 1 - Math.pow(1 - t, 3);
}
function easeInOut(t) {
  return t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
}
